import { Component, computed, inject, Signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PhoneNumberAvailability, RegistrationStage, RegulationStatus } from '@vendasta/protos/sms/v2/enums';
import type { GetPhoneAndRegistrationStateResponse, RegulationInfo } from '@vendasta/protos/sms/v2/types';
import { map } from 'rxjs';
import { OwnerService } from '../../../common/owner.service';
import { ProgressTrackerComponent, StageStatus, Status } from '../../progress-tracker/progress-tracker.component';
import { countryFromCode, hasNumberProvisioningStep } from '../../../constants';
import { getPhoneProvisioningMessageKey } from '../phone-provisioning-helper-functions';
import { RegulationService } from '../regulation.service';
import { RegistrationService } from '../../registration.service';

const STAGES_KEY = 'SMS_REGULATION.COMMON.STATUS.STAGES';
const RESOURCE_TYPE_KEY = 'SMS_REGULATION.COMMON.STATUS.RESOURCE_TYPE';
const TWILIO_ERROR_KEY = 'SMS_REGULATION.COMMON.STATUS.TWILIO_ERROR';
const APPROVED_MESSAGE_KEY = 'SMS_REGULATION.COMMON.STATUS.APPROVED_MESSAGE';

/** Maps the server-resolved phone availability to the 4th stepper stage status. */
function phoneAvailabilityToStatus(availability: PhoneNumberAvailability | undefined): Status {
  switch (availability) {
    case PhoneNumberAvailability.PHONE_NUMBER_AVAILABILITY_ASSIGNING:
      return Status.InProgress;
    case PhoneNumberAvailability.PHONE_NUMBER_AVAILABILITY_FAILED:
      return Status.Failed;
    case PhoneNumberAvailability.PHONE_NUMBER_AVAILABILITY_ACTIVE:
      return Status.Completed;
    case PhoneNumberAvailability.PHONE_NUMBER_AVAILABILITY_NOT_STARTED:
    case PhoneNumberAvailability.PHONE_NUMBER_AVAILABILITY_UNSPECIFIED:
    default:
      return Status.NotStarted;
  }
}

@Component({
  selector: 'sms-regulatory-status',
  imports: [MatCardModule, GalaxyAlertModule, ProgressTrackerComponent, TranslateModule],
  templateUrl: './sms-regulatory-status.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sms-regulatory-status.component.scss',
})
export class SmsRegulatoryStatusComponent {
  private readonly regulationService = inject(RegulationService);
  private readonly registrationService = inject(RegistrationService);
  private readonly ownerService = inject(OwnerService);
  private readonly translate = inject(TranslateService);

  /** Rejection detail text lives only in RegulationInfo — the same shared call that prefills the form. */
  protected readonly regulationInfo: Signal<RegulationInfo | null> = toSignal(this.regulationService.regulationInfo$, {
    initialValue: null,
  });

  /** Server-resolved registration + phone provisioning state; drives the stepper stage statuses. */
  private readonly state: Signal<GetPhoneAndRegistrationStateResponse | undefined> =
    this.registrationService.phoneAndRegistrationState;

  private readonly hasProvisioningStep: Signal<boolean> = toSignal(
    this.ownerService.getOwnerLocation$().pipe(map((location) => hasNumberProvisioningStep(countryFromCode(location.country)))),
    { initialValue: false },
  );

  protected readonly stages: Signal<StageStatus[]> = computed(() =>
    this.buildStages(this.state(), this.hasProvisioningStep()),
  );

  protected readonly approvedStatusMessage: Signal<string> = computed(() =>
    this.buildApprovedStatusMessage(this.state(), this.hasProvisioningStep()),
  );

  protected readonly RegulationStatus = RegulationStatus;

  protected resourceTypeLabel(resourceType: string): string {
    if (!resourceType) {
      return this.translate.instant(`${RESOURCE_TYPE_KEY}.UNKNOWN`);
    }
    const key = `${RESOURCE_TYPE_KEY}.${resourceType.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated === key ? resourceType : translated;
  }

  protected rejectionMessage(errorCodes: readonly number[] | undefined, fallback: string): string {
    if (!errorCodes?.length) {
      return fallback;
    }
    const translated = errorCodes
      .map((code) => {
        const key = `${TWILIO_ERROR_KEY}.${code}`;
        const value = this.translate.instant(key);
        return value === key ? null : value;
      })
      .filter((s): s is string => s !== null);
    return translated.length > 0 ? translated.join(' ') : fallback;
  }

  private stageLabel(key: string): string {
    return this.translate.instant(`${STAGES_KEY}.${key}`);
  }

  private buildApprovedStatusMessage(
    state: GetPhoneAndRegistrationStateResponse | undefined,
    hasProvisioningStep: boolean,
  ): string {
    if (!hasProvisioningStep) {
      return this.translate.instant(APPROVED_MESSAGE_KEY);
    }
    const stageStatus = phoneAvailabilityToStatus(state?.phone?.status);
    const messageKey = getPhoneProvisioningMessageKey(stageStatus) ?? APPROVED_MESSAGE_KEY;
    return this.translate.instant(messageKey);
  }

  private buildStages(
    state: GetPhoneAndRegistrationStateResponse | undefined,
    hasProvisioningStep: boolean,
  ): StageStatus[] {
    const stages = this.buildRegulationStages(state?.stage);
    if (!hasProvisioningStep) {
      return stages;
    }
    // Provisioning countries: stage 3 is relabeled "Business information", plus a 4th provisioning stage.
    stages[2] = { name: this.stageLabel('BUSINESS_INFORMATION'), status: stages[2].status };
    stages.push({
      name: this.stageLabel('PHONE_NUMBER_PROVISIONING'),
      status: phoneAvailabilityToStatus(state?.phone?.status),
    });
    return stages;
  }

  private buildRegulationStages(stage: RegistrationStage | undefined): StageStatus[] {
    switch (stage) {
      case RegistrationStage.REGISTRATION_STAGE_COMPLETE:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.Completed },
          { name: this.stageLabel('APPROVED'), status: Status.Completed },
        ];
      case RegistrationStage.REGISTRATION_STAGE_SUBMISSION_ERROR:
      case RegistrationStage.REGISTRATION_STAGE_CAMPAIGN_ERROR:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.Failed },
          { name: this.stageLabel('REJECTED'), status: Status.Failed },
        ];
      case RegistrationStage.REGISTRATION_STAGE_REVIEWING:
        return [
          { name: this.stageLabel('INFORMATION_SUBMITTED'), status: Status.Completed },
          { name: this.stageLabel('REVIEW'), status: Status.InProgress },
          { name: this.stageLabel('APPROVED'), status: Status.NotStarted },
        ];
      case RegistrationStage.REGISTRATION_STAGE_NOT_SUBMITTED:
      case RegistrationStage.REGISTRATION_STAGE_UNSPECIFIED:
      default:
        return [
          { name: this.stageLabel('SUBMIT_INFORMATION'), status: Status.NotStarted },
          { name: this.stageLabel('REVIEW'), status: Status.NotStarted },
          { name: this.stageLabel('APPROVED'), status: Status.NotStarted },
        ];
    }
  }
}
